import React from "react";
import verificationImage from "../../assets/verification.webp";
import fastFundedImage from "../../assets/submit.webp";
import payoutImage from "../../assets/payout.webp";
import matchingImage from "../../assets/matching.webp";

function Principles() {
  return (
    <section className="overflow-hidden bg-[#f8faf9] section-spacing">
      <div className="section-container">
        {/* Section Label */}
        <p className="font-jakarta text-[11px] sm:text-[12px] font-medium tracking-[0.34em] text-[var(--hero-green)] uppercase mb-2.5">
          PRINCIPLES
        </p>

        {/* ================= MOBILE VIEW (Sequential Stack) ================= */}
        <div className="mt-12 space-y-12 sm:mt-14 sm:space-y-14 md:hidden">
          {/* Item 1 */}
          <article className="flex items-center gap-5 text-left">
            <div className="flex w-[60px] shrink-0 items-center justify-center">
              <img
                src={verificationImage}
                alt="Verification shield"
                className="h-auto w-[52px] object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="min-w-0">
              <h3 className="font-bricolage text-[20px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720]">
                Verified over assumed
              </h3>
              <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369]">
                A file moves only after the checks pass.
              </p>
            </div>
          </article>

          {/* Item 2 */}
          <article className="flex items-center gap-5 text-left">
            <div className="flex w-[60px] shrink-0 items-center justify-center">
              <img
                src={fastFundedImage}
                alt="Fast clock speed"
                className="h-auto w-[52px] object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="min-w-0">
              <h3 className="font-bricolage text-[20px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720]">
                Funded fast, in-house where possible
              </h3>
              <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369]">
                Our own book takes the file when it fits.
              </p>
            </div>
          </article>

          {/* Item 3 */}
          <article className="flex items-center gap-5 text-left">
            <div className="flex w-[60px] shrink-0 items-center justify-center">
              <img
                src={payoutImage}
                alt="Payout coins"
                className="h-auto w-[52px] object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="min-w-0">
              <h3 className="font-bricolage text-[20px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720]">
                Transparent payouts
              </h3>
              <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369]">
                Commission tied to a disbursal, visible to the DSA.
              </p>
            </div>
          </article>

          {/* Item 4 */}
          <article className="flex items-center gap-5 text-left">
            <div className="flex w-[60px] shrink-0 items-center justify-center">
              <img
                src={matchingImage}
                alt="Matching puzzle connection"
                className="h-auto w-[52px] object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="min-w-0">
              <h3 className="font-bricolage text-[20px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720]">
                Neutral marketplace by design
              </h3>
              <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369]">
                Ranking follows fit, not who owns the platform.
              </p>
            </div>
          </article>
        </div>

        {/* ================= DESKTOP VIEW (Staggered Waterfall 1 -> 2 -> 3 -> 4) ================= */}
        <div className="mt-16 hidden grid-cols-2 gap-x-12 lg:gap-x-20 md:grid">
          {/* Left Column (Items 1 and 3) */}
          <div className="flex flex-col">
            {/* Item 1: Top Left */}
            <article className="flex items-center gap-6 text-left">
              <div className="flex w-[68px] shrink-0 items-center justify-center lg:w-[74px]">
                <img
                  src={verificationImage}
                  alt="Verification shield"
                  className="h-auto w-[58px] object-contain transition-transform duration-200 hover:scale-105 lg:w-[66px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-bricolage text-[22px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720] lg:text-[24px]">
                  Verified over assumed
                </h3>
                <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369] lg:text-[15px]">
                  A file moves only after the checks pass.
                </p>
              </div>
            </article>

            {/* Item 3: Staggered Below Item 2 */}
            <article className="mt-32 flex items-center gap-6 text-left lg:mt-40">
              <div className="flex w-[68px] shrink-0 items-center justify-center lg:w-[74px]">
                <img
                  src={payoutImage}
                  alt="Payout coins"
                  className="h-auto w-[58px] object-contain transition-transform duration-200 hover:scale-105 lg:w-[66px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-bricolage text-[22px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720] lg:text-[24px]">
                  Transparent payouts
                </h3>
                <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369] lg:text-[15px]">
                  Commission tied to a disbursal, visible to the DSA.
                </p>
              </div>
            </article>
          </div>

          {/* Right Column (Items 2 and 4, offset downwards) */}
          <div className="flex flex-col pt-24 lg:pt-28">
            {/* Item 2: Sits Below Item 1 */}
            <article className="flex items-center justify-end gap-6 text-right">
              <div className="min-w-0">
                <h3 className="font-bricolage text-[22px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720] lg:text-[24px]">
                  Funded fast, in-house where possible
                </h3>
                <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369] lg:text-[15px]">
                  Our own book takes the file when it fits.
                </p>
              </div>
              <div className="flex w-[68px] shrink-0 items-center justify-center lg:w-[74px]">
                <img
                  src={fastFundedImage}
                  alt="Fast clock speed"
                  className="h-auto w-[58px] object-contain transition-transform duration-200 hover:scale-105 lg:w-[66px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </article>

            {/* Item 4: Staggered Below Item 3 */}
            <article className="mt-32 flex items-center justify-end gap-6 text-right lg:mt-40">
              <div className="min-w-0">
                <h3 className="font-bricolage text-[22px] font-semibold leading-tight tracking-[-0.4px] text-[#0b1720] lg:text-[24px]">
                  Neutral marketplace by design
                </h3>
                <p className="mt-2 font-jakarta text-[14px] leading-[1.6] text-[#556369] lg:text-[15px]">
                  Ranking follows fit, not who owns the platform.
                </p>
              </div>
              <div className="flex w-[68px] shrink-0 items-center justify-center lg:w-[74px]">
                <img
                  src={matchingImage}
                  alt="Matching puzzle connection"
                  className="h-auto w-[58px] object-contain transition-transform duration-200 hover:scale-105 lg:w-[66px]"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Principles;
